import type { ExpenseCategory, ExpensePaymentMethod, ExpenseStatus, ExpenseTaxMode } from '../types/expenses';

export interface ExpenseActionVisibility {
  canEdit: boolean;
  canDeleteDraft: boolean;
  canConfirm: boolean;
  canCancel: boolean;
}

/**
 * Fase 18A: qué acciones de escritura debe ofrecer la pantalla de Otros Gastos según el estado del
 * gasto. Extraída como función pura (mismo criterio que purchaseActions.ts) para poder probar la
 * regla sin renderizar componentes — este proyecto no tiene infraestructura de pruebas de UI.
 *
 * - DRAFT: se puede editar, eliminar físicamente y confirmar.
 * - CONFIRMED: no se puede editar ni eliminar físicamente — solo anular (con motivo obligatorio).
 * - CANCELLED: de solo lectura — no editar, no eliminar, no confirmar, no anular de nuevo.
 */
export function getExpenseActions(status: ExpenseStatus): ExpenseActionVisibility {
  return {
    canEdit: status === 'DRAFT',
    canDeleteDraft: status === 'DRAFT',
    canConfirm: status === 'DRAFT',
    canCancel: status === 'CONFIRMED',
  };
}

// Fase 18B: UTILITIES → "Servicios" y SERVICES → "Honorarios" (antes "Servicios profesionales") —
// ambas son las dos categorías del mockup que tienen subcategorías (ver EXPENSE_SUBCATEGORY_SUGGESTIONS).
export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  RENT: 'Renta',
  UTILITIES: 'Servicios',
  PAYROLL: 'Sueldos',
  MAINTENANCE: 'Mantenimiento',
  TRANSPORT: 'Transporte',
  MARKETING: 'Publicidad',
  COMMISSIONS: 'Comisiones',
  SUPPLIES: 'Insumos no inventariables',
  CLEANING: 'Limpieza',
  SERVICES: 'Honorarios',
  OTHER: 'Otro',
};

export const EXPENSE_PAYMENT_METHOD_LABELS: Record<ExpensePaymentMethod, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  TRANSFER: 'Transferencia',
  OTHER: 'Otro',
};

export const EXPENSE_STATUS_LABELS: Record<ExpenseStatus, string> = {
  DRAFT: 'Borrador',
  CONFIRMED: 'Confirmado',
  CANCELLED: 'Anulado',
};

export const EXPENSE_TAX_MODE_LABELS: Record<ExpenseTaxMode, string> = {
  NONE: 'Sin IVA',
  INCLUDED: 'IVA Incluido',
  CUSTOM: 'Otros/Especificar',
};

// Fase 18B: categorías que tienen subcategoría en el formulario — coincide con el mockup
// (Servicios/Honorarios). Las demás categorías no muestran el campo "Subcategoría".
export const EXPENSE_CATEGORIES_WITH_SUBCATEGORY: ExpenseCategory[] = ['UTILITIES', 'SERVICES'];

// Sugerencias iniciales que siempre aparecen en el dropdown de subcategoría, además de las que el
// negocio haya creado (guardadas en la base de datos). No se insertan en la base de datos: si el
// usuario elige una de estas, se manda tal cual como texto — solo se guardan en la base las que se
// crean explícitamente con "+ Agregar" (ver ExpenseSubcategoryModal en ExpensesPage.tsx).
export const EXPENSE_SUBCATEGORY_SUGGESTIONS: Partial<Record<ExpenseCategory, string[]>> = {
  UTILITIES: ['Luz', 'Internet', 'Agua', 'Gas', 'Teléfono', 'Cable/TV', 'Alarma', 'Otro'],
  SERVICES: ['Contador', 'Abogado', 'Consultor', 'Diseñador', 'Otro'],
};
